import { set, isNil, isNaN, isEmpty, each } from 'lodash';
import { defineMessages } from 'react-intl';
import { isMongoId } from '../../../../lib/formValidation';

const intlMessages = defineMessages({
    selectCabinetError: {
        id: 'pages.schedule.form.AppointmentForm.selectCabinetError',
        defaultMessage: 'Select cabinet from the list',
    },
    selectMasterError: {
        id: 'pages.schedule.form.AppointmentForm.selectMasterError',
        defaultMessage: 'Select master from the list',
    },
    dateError: {
        id: 'pages.schedule.form.AppointmentForm.dateError',
        defaultMessage: 'Enter appointment date',
    },
    timeSpanError: {
        id: 'pages.schedule.form.AppointmentForm.timeSpanError',
        defaultMessage: 'Select time span',
    },
    durationError: {
        id: 'pages.schedule.form.AppointmentForm.durationError',
        defaultMessage: 'Select procedure duration time',
    },
    serviceTypeError: {
        id: 'pages.schedule.form.AppointmentForm.serviceTypeError',
        defaultMessage: 'Select service type',
    },
    selectClientError: {
        id: 'pages.schedule.form.AppointmentForm.selectClientError',
        defaultMessage: 'Select client OR create a new',
    },
    timeOutOfRangeError: {
        id: 'pages.schedule.form.AppointmentForm.timeOutOfRangeError',
        defaultMessage: 'Time is out of range',
    },
});

/**
 * react-hook-form resolver for EditAppointment.
 *
 * Context: { intl, inCabinet, scheduleEndAt } — passed via useForm's
 * `context` option so the resolver always sees the current values.
 */
export default (values, context = {}) => {
    const { intl, inCabinet, scheduleEndAt } = context;
    const errors = {};
    const fieldError = message => ({
        type: 'validate',
        message: intl.formatMessage(message),
    });

    if (inCabinet) {
        if (!isMongoId(values.cabinet)) {
            set(errors, 'cabinet', fieldError(intlMessages.selectCabinetError));
        }
    } else {
        // Validate service master
        if (!isMongoId(values.master)) {
            set(errors, 'master', fieldError(intlMessages.selectMasterError));
        }
    }

    // Validate appointment date
    if (isNil(values.startDate)) {
        set(errors, 'startDate', fieldError(intlMessages.dateError));
    }

    // Validate time span
    if (isNil(values.startTime)) {
        set(errors, 'startTime', fieldError(intlMessages.timeSpanError));
    }

    // Validate duration
    if (isNil(values.duration)) {
        set(errors, 'duration', fieldError(intlMessages.durationError));
    }

    // Validate the service type
    if (!isMongoId(values.service)) {
        set(errors, 'service', fieldError(intlMessages.serviceTypeError));
    }

    // Validate service client
    if (!isMongoId(values.client)) {
        set(errors, 'client', fieldError(intlMessages.selectClientError));
    }

    if (
        !isNil(values.startDate) &&
        !isNil(values.startTime) &&
        !isNil(values.duration)
    ) {
        // Verify that service end time is not out of range of salon working time
        const endAt = values.startTime + values.duration;
        if (endAt > scheduleEndAt) {
            set(
                errors,
                'startTime',
                fieldError(intlMessages.timeOutOfRangeError),
            );
        }
    }

    if (values.services && values.services.length) {
        each(
            values.services,
            ({ cabinet, master, startTime, duration, service }, i) => {
                if (inCabinet) {
                    if (!isMongoId(cabinet)) {
                        set(
                            errors,
                            ['services', i, 'cabinet'],
                            fieldError(intlMessages.selectCabinetError),
                        );
                    }
                } else {
                    // Validate service master
                    if (!isMongoId(master)) {
                        set(
                            errors,
                            ['services', i, 'master'],
                            fieldError(intlMessages.selectMasterError),
                        );
                    }
                }

                // Validate time span
                if (isNil(startTime) || isNaN(startTime)) {
                    set(
                        errors,
                        ['services', i, 'startTime'],
                        fieldError(intlMessages.timeSpanError),
                    );
                }

                // Validate duration
                if (isNil(duration)) {
                    set(
                        errors,
                        ['services', i, 'duration'],
                        fieldError(intlMessages.durationError),
                    );
                }

                // Validate the service type
                if (!isMongoId(service)) {
                    set(
                        errors,
                        ['services', i, 'service'],
                        fieldError(intlMessages.serviceTypeError),
                    );
                }

                if (!isNil(startTime) && !isNil(duration)) {
                    // Verify that service end time is not out of range of
                    // salon working time
                    const endAt = startTime + duration;
                    if (endAt > scheduleEndAt) {
                        set(
                            errors,
                            ['services', i, 'startTime'],
                            fieldError(intlMessages.timeOutOfRangeError),
                        );
                    }
                }
            },
        );
    }

    return isEmpty(errors) ? { values, errors: {} } : { values: {}, errors };
};
